import { useMemo } from 'react';
import type { MatchListValue } from '@/components/tree-dnd/tree-dnd-types';
import { MatchListControl } from '@/components/tree-dnd/tree-dnd-conditions/match-list-control';
import type { MatchListOption } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { Button } from '@/components/ui/button';
import {
  ProductsApiError,
  useTenantProducts,
} from '@/hooks/use-tenant-products';
import { useI18nContext } from '@/i18n/i18n-react';

interface ProductMatchListProps {
  value: MatchListValue;
  onChange: (value: MatchListValue) => void;
  matchModeHelp?: {
    any: string;
    all: string;
  };
  /** When false, hides the any/all control (use with external match-mode UI). */
  allowAll?: boolean;
}

export function useProductMatchListOptions(
  selectedIds: string[]
): MatchListOption[] {
  const { LL } = useI18nContext();
  const productsQuery = useTenantProducts();

  return useMemo(() => {
    const live = (productsQuery.data ?? []).map(
      (product): MatchListOption => ({
        id: product.ecomPlatformProductId,
        label: product.name,
        previewImageUrl: product.previewImageUrl,
      })
    );
    const liveIds = new Set(live.map((option) => option.id));
    const ghosts = selectedIds
      .filter((id) => !liveIds.has(id))
      .map(
        (id): MatchListOption => ({
          id,
          label: LL.audiences.filters.products.unavailableProduct(),
          previewImageUrl: null,
          unavailable: true,
        })
      );
    return [...ghosts, ...live];
  }, [LL, productsQuery.data, selectedIds]);
}

export function ProductMatchList({
  value,
  onChange,
  matchModeHelp,
  allowAll = true,
}: ProductMatchListProps) {
  const { LL } = useI18nContext();
  const productsQuery = useTenantProducts();
  const options = useProductMatchListOptions(value.ids);

  const error = productsQuery.error;
  const apiError =
    error instanceof ProductsApiError
      ? error
      : error
        ? new ProductsApiError('UPSTREAM_ERROR', error.message, 502)
        : null;

  return (
    <div className="flex flex-col gap-3">
      <MatchListControl
        variant="expanded"
        value={value}
        onChange={onChange}
        options={options}
        isLoading={productsQuery.isLoading}
        matchModeHelp={matchModeHelp}
        allowAll={allowAll}
      />

      {apiError ? (
        <div className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <p>
            {apiError.code === 'NO_INTEGRATION'
              ? LL.audiences.filters.products.noIntegration({
                  message: apiError.message,
                })
              : LL.audiences.filters.products.loadError({
                  message: apiError.message,
                })}
          </p>
          {apiError.code !== 'NO_INTEGRATION' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() => void productsQuery.refetch()}
            >
              {LL.audiences.filters.products.retry()}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
